<template>
  <ModalVue :modal-active="isOpen" @close="closeModal" maxWidth="max-w-[900px]">
    <div>
      <h3 class="text-lg font-bold text-textPrimary text-left">
        Termo de Ciência e Concordância da Política de Responsabilidade sobre as atividades
        assistenciais assumidas junto à sociedade médica gestora da unidade
      </h3>

      <div class="flex flex-col text-left">
        <div>
          <nav class="flex text-center justify-center" aria-label="Tabs">
            <div
              :class="[
                'border-b-2 w-full',
                activeTab === 'passou' ? 'border-[#2F7F91]' : 'border-transparent'
              ]"
            >
              <button
                @click="activeTab = 'passou'"
                :class="[
                  'py-4 px-2 text-sm transition-colors focus:outline-none',
                  activeTab === 'passou'
                    ? 'text-textPrimary font-bold'
                    : 'text-gray-500 hover:text-gray-700'
                ]"
              >
                Passou
              </button>
            </div>

            <div
              :class="[
                'border-b-2 w-full',
                activeTab === 'assumiu' ? 'border-[#2F7F91]' : 'border-transparent'
              ]"
            >
              <button
                @click="activeTab = 'assumiu'"
                :class="[
                  'py-4 px-2 text-sm transition-colors focus:outline-none',
                  activeTab === 'assumiu'
                    ? 'text-textPrimary font-bold'
                    : 'text-gray-500 hover:text-gray-700'
                ]"
              >
                Assumiu
              </button>
            </div>
          </nav>
        </div>

        <div ref="termRef" class="py-6 text-sm leading-relaxed text-textPrimary">
          <PassedTabContent
            v-if="activeTab === 'passou'"
            :current-signer-name="currentSignerName"
            :signed-date="signedDate"
          />
          <AssumedTabContent
            v-if="activeTab === 'assumiu'"
            :current-signer-name="currentSignerName"
            :signed-date="signedDate"
          />
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex gap-4 items-center w-full border-t pt-4">
        <div class="relative">
          <div @click="handleExport">
            <FilledButton
              text="Exportar"
              width="120px"
              height="48px"
              class="bg-primary30"
              :prepend-icon="mdiMenuDown"
            />
          </div>
          <div
            v-if="showExportMenu"
            class="absolute left-0 bottom-full mb-2 w-56 origin-top-right rounded-md bg-white shadow-lg border border-gray-200 focus:outline-none z-20"
          >
            <div>
              <a
                class="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
                @click="exportAsPdf"
              >
                <pdfIcon width="20" height="20" :color="iconColor" />
                <span>Exportar em .pdf</span>
              </a>
              <a
                class="flex cursor-pointer items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100"
                @click="exportAsJpg"
              >
                <imageIcon width="20" height="20" :color="iconColor" />
                <span>Exportar em .jpg</span>
              </a>
            </div>
          </div>
        </div>

        <div class="text-left text-sm text-textPrimary">
          <div>
            <p>Assinado em {{ signedDate }}</p>
            <p>por {{ currentSignerName }}</p>
          </div>
        </div>
      </div>
    </template>
  </ModalVue>
</template>

<script setup lang="ts">
import { ref, defineAsyncComponent, nextTick, computed } from 'vue'
import ModalVue from '@/components/designSystem/modals/Modal.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { mdiMenuDown } from '@mdi/js'
import { toJpeg } from 'html-to-image'
import jsPDF from 'jspdf'
import type { HandoverInviteResponse } from '@/Network/Types/Responses/HandoverInviteResponses'
import { format } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'

import PassedTabContent from './PassedTabContent.vue'
import AssumedTabContent from './AssumedTabContent.vue'

const iconColor = '#000000'
const pdfIcon = defineAsyncComponent(() => import('@/assets/icons/pdf.vue'))
const imageIcon = defineAsyncComponent(() => import('@/assets/icons/image.vue'))

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  historyItem: {
    type: Object as () => HandoverInviteResponse | null,
    default: null
  }
})

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
}>()

const activeTab = ref<'passou' | 'assumiu'>('passou')

const signedDate = computed(() => {
  if (!props.historyItem?.updatedAt) {
    return 'Data não disponível'
  }
  try {
    const brasiliaTimezone = 'America/Sao_Paulo'
    const utcDate = new Date(props.historyItem.updatedAt)
    const zonedDate = utcToZonedTime(utcDate, brasiliaTimezone)
    return format(zonedDate, "dd/MM/yyyy 'às' HH:mm ('Horário de Brasília UTC-3')")
  } catch (error) {
    console.error('Erro ao formatar data:', error)
    return 'Data inválida'
  }
})

const signedByDoctorOrigin = computed(() => {
  const doctorName = props.historyItem?.doctorOrigin?.mainCrm?.name
  return doctorName || 'Médico (origem) não identificado'
})

const signedByDoctorDestiny = computed(() => {
  const doctorName = props.historyItem?.doctorDestiny?.mainCrm?.name
  return doctorName || 'Médico (destino) não identificado'
})

const currentSignerName = computed(() => {
  return activeTab.value === 'passou' ? signedByDoctorOrigin.value : signedByDoctorDestiny.value
})

const showExportMenu = ref(false)

function closeModal() {
  emit('update:isOpen', false)
}

function handleExport() {
  showExportMenu.value = !showExportMenu.value
}

const termRef = ref<HTMLElement | null>(null)

async function exportAsPdf() {
  showExportMenu.value = false
  await nextTick()
  const el = termRef.value
  if (!el) return
  const dataUrl = await toJpeg(el, {
    quality: 0.98,
    backgroundColor: '#ffffff',
    pixelRatio: Math.min(window.devicePixelRatio || 1, 2)
  })
  const img = new Image()
  img.src = dataUrl
  await img.decode().catch(() => {})
  const iw = img.naturalWidth || el.clientWidth
  const ih = img.naturalHeight || el.clientHeight
  const orientation = iw >= ih ? 'landscape' : 'portrait'
  const pdf = new jsPDF({ orientation, unit: 'px', format: [iw, ih] })
  pdf.addImage(dataUrl, 'JPEG', 0, 0, iw, ih)
  pdf.save(`termo-${new Date().toISOString().slice(0, 10)}.pdf`)
}

async function exportAsJpg() {
  showExportMenu.value = false
  await nextTick()
  const el = termRef.value
  if (!el) return
  const dataUrl = await toJpeg(el, { quality: 0.95, backgroundColor: '#fff' })
  const a = document.createElement('a')
  a.href = dataUrl
  a.download = `termo-${new Date().toISOString().slice(0, 10)}.jpg`
  a.click()
}
</script>
